import React from 'react';
import { FormGroup } from 'react-bootstrap';
import { File, FilePreprocessStatus, RootState } from 'v2/models';
import classnames from 'classnames';
import styles from './preprocessFileMessage.module.scss';
import RepeatArrowIcon from 'v2/icons/repeat_arrow.svg';
import ErrorIcon from 'v2/icons/error_warning.svg';
import { useQuery, useQueryClient } from 'react-query';
import restFactory from 'v2/utils/restFactory';
import { isFileRunning } from 'v2/utils/drmFileUtils';
import { FILE_POLL_INTERVAL } from 'utils/constants';
import { PERMISSION_NAME, hasPermission } from 'v2/utils/permissions';
import { useSelector } from 'react-redux';

const PreprocessFileMessage = ({ file }: { file: File }) => {
  const queryClient = useQueryClient();
  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions
  );

  const hasChatPermission = hasPermission(permissions, [
    PERMISSION_NAME.DOCUMENT_CHAT,
  ]);
  const isPolling = (file?: File) => hasChatPermission && isFileRunning(file);
  const showPreprocessMessage =
    hasChatPermission &&
    [FilePreprocessStatus.ERROR, FilePreprocessStatus.RUNNING].includes(
      file.preprocess_status
    );

  useQuery(
    ['Get file by ID', file.id],
    async () => await restFactory.get<File>(`/dj/api/v1/file/${file.id}/`),
    {
      cacheTime: 0,
      enabled: isPolling(file),
      refetchOnMount: false,
      refetchInterval: (data?: File) => {
        return isPolling(data) ? FILE_POLL_INTERVAL : false;
      },
      onSuccess: (data: File) => {
        if (data.preprocess_status !== file.preprocess_status) {
          queryClient.invalidateQueries('FilesTabList');
        }
      },
    }
  );

  if (!showPreprocessMessage) {
    return null;
  }

  return (
    <FormGroup className="mt-4">
      <div
        role="alert"
        className={classnames('d-flex flex-column', styles.container, {
          [styles.error]: file.preprocess_status === FilePreprocessStatus.ERROR,
        })}
      >
        <div
          className={classnames(
            'd-flex flex-wrap align-items-center',
            styles.check
          )}
        >
          {file.preprocess_status === FilePreprocessStatus.ERROR ? (
            <img src={ErrorIcon} alt={'Error alert icon'}></img>
          ) : (
            <img
              className={classnames('ml-1', styles.loadingIcon)}
              src={RepeatArrowIcon}
              alt={'Repeat alert icon'}
            ></img>
          )}
          <span className={classnames(styles.title)}>
            {file.preprocess_status === FilePreprocessStatus.ERROR
              ? 'Issue preparing file for Document Search'
              : 'Preparing file for Document Search'}
          </span>
        </div>
      </div>
    </FormGroup>
  );
};

export default PreprocessFileMessage;
